import { useToast } from '@cad-arc';
import { useMutation } from '@tanstack/react-query';
import { createApiErrorHandler } from '@m43/core';
import { useApiClient } from '~/api-client';
import { ApiResult, CadPrintResource, Printable, PrintingDataTypeEnum } from '@mark43/cad-api';
import { useBootstrapState } from '../../bootstrap/bootstrap-provider';
import { errorOccurredWhile } from '../../common/helpers/error-occurred-while';
import { useExportsApi } from '../../exports/use-exports-api';
import { AxiosError } from 'axios-cad-nx';
import { useCallback } from 'react';

export function useGenerateEventPDF() {
    const client = useApiClient();
    const { cadDepartmentAgencyView } = useBootstrapState();
    const toast = useToast();
    const { refetchExports } = useExportsApi();

    const generatePdfMutation = useMutation(
        async ({
            eventId,
            eventNumber,
            includeAttachments,
        }: {
            eventId: number;
            eventNumber: string;
            includeAttachments: boolean;
        }) => {
            const generatePrintable = async () => {
                return client
                    .request<CadPrintResource.GetEventPrintables>({
                        method: 'POST',
                        url: `cad_print/agency_event/printables/${eventId}/`,
                    })
                    .then((resp) => resp.data.data?.[0]);
            };

            const generateExport = (printable: Printable, withAttachments: boolean = false) => {
                return client.request<CadPrintResource.StartMillingJob>({
                    method: 'POST',
                    url: `cad_print`,
                    data: [
                        // Name reference: https://github.com/mark43/cad/blob/main/cobalt-core-client/paper-mill-client/src/main/java/com/mark43/papermill/models/WorkOrder.java
                        {
                            attachmentsConfig: withAttachments
                                ? {
                                      // all fields required
                                      attachmentIds: [],
                                      excludedAttachmentTypes: [],
                                      excludedFolderIds: [],
                                      forceZip: false,
                                      includeAll: true,
                                      // set to false to prevent pdf attachments from getting merged into event / bolo pdf
                                      mergeAttachments: false,
                                  }
                                : undefined,
                            // Relevant args
                            mim: { CAD_AGENCY_EVENT: eventId },
                            dt: PrintingDataTypeEnum.AGENCY_EVENT.name,
                            f: 0,
                            pf: 'PDF',
                            ptId: printable.printingTemplateId,
                            t: printable.title,

                            // Required args
                            ar: false,
                            c: [],
                            cecIds: [],
                            dId: cadDepartmentAgencyView.departmentId,
                            // @ts-expect-error don't want to set this
                            eId: undefined,
                            ec: {},
                            fileName: `Event #${eventNumber}${withAttachments ? ' with attachments' : ''}`,
                            ii: false,
                        },
                    ],
                });
            };

            try {
                const printable = await generatePrintable();

                if (!printable) {
                    toast({
                        status: 'default',
                        description: 'Could not export event to PDF',
                    });
                } else {
                    await generateExport(printable, includeAttachments);
                    refetchExports();
                }
            } catch (error) {
                const handleError = createApiErrorHandler(() =>
                    toast({
                        status: 'error',
                        description: errorOccurredWhile('exporting event'),
                    })
                );
                handleError(error as AxiosError<ApiResult, unknown>);
            }
        }
    );

    const generatePDFWithoutAttachments = useCallback(
        function generatePDFWithoutAttachments(eventId: number, eventNumber: string): void {
            generatePdfMutation.mutate({ eventId, eventNumber, includeAttachments: false });
        },
        [generatePdfMutation]
    );

    const generatePDFWithAttachments = useCallback(
        function generatePDFWithAttachments(eventId: number, eventNumber: string): void {
            generatePdfMutation.mutate({ eventId, eventNumber, includeAttachments: true });
        },
        [generatePdfMutation]
    );

    return {
        generatePDFWithoutAttachments,
        generatePDFWithAttachments,
        isLoading: generatePdfMutation.isLoading,
    };
}
